import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveConnector,
  listConnectors,
  type OutboundConnector,
} from '../api/connectors.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

export function ConnectorsList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<OutboundConnector | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['connectors', id, { includeArchived }],
    queryFn: () => listConnectors(id, { includeArchived }),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: (connectorId: string) => archiveConnector(id, connectorId),
    onSuccess: () => {
      pushToast('success', t.connector.archived);
      queryClient.invalidateQueries({ queryKey: ['connectors', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const connectors = data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.connector.listTitle}</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.connector.showArchived}
          </label>
          <Link
            to={`/tenants/${id}/connectors/new`}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            {t.connector.newBtn}
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && connectors.length === 0 && (
        <p className="text-slate-500">{t.connector.empty}</p>
      )}

      {connectors.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">{t.connector.fields.name}</th>
                <th className="px-4 py-2">{t.connector.fields.provider}</th>
                <th className="px-4 py-2">{t.connector.fields.status}</th>
                <th className="px-4 py-2">{t.connector.fields.displayPhoneNumber}</th>
                <th className="px-4 py-2">{t.connector.fields.version}</th>
                <th className="px-4 py-2 text-right">{t.connector.fields.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {connectors.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`connector-row-${c.id}`}
                  className={c.deletedAt ? 'opacity-50' : undefined}
                >
                  <td className="px-4 py-2 font-medium">{c.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{c.provider}</td>
                  <td className="px-4 py-2">
                    <StatusBadge
                      label={t.connector.status[c.status]}
                      tone={c.status === 'connected' ? 'green' : 'red'}
                    />
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {c.displayPhoneNumber ?? c.businessPhone ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-slate-500">v{c.version}</td>
                  <td className="px-4 py-2 text-right">
                    {!c.deletedAt && (
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/tenants/${id}/connectors/${c.id}/edit`}
                          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                        >
                          {t.connector.edit}
                        </Link>
                        <button
                          type="button"
                          onClick={() => setConfirmTarget(c)}
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                        >
                          {t.connector.archive}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title={t.connector.confirmArchiveTitle}
        description={t.connector.confirmArchiveDesc}
        confirmLabel={t.connector.archive}
        cancelLabel={t.app.cancel}
        loading={archiveMutation.isPending}
        onConfirm={() => {
          if (confirmTarget) archiveMutation.mutate(confirmTarget.id);
        }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
